import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { Modal, Spinner, Card, Badge, Form } from 'react-bootstrap'
import { MdDeleteForever } from 'react-icons/md'
import ReactPlayer from 'react-player'
import Select from 'react-select'
import {
    VendorAdGetAllEnd,
    VendorAdScreensEnd,
    VendorAdStoreByScreenEnd,
    VendorAdScheduleEnd,
    VendorAdTimingEnd
} from '../../../constants/api.constants'
import Toast from '../../../utils/Toast/Toast'
import './VendorScheduleAdModal.scss'

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
}

const move = (source, destination, droppableSource, droppableDestination) => {
    const sourceClone = Array.from(source)
    const destClone = Array.from(destination)
    const [removed] = sourceClone.splice(droppableSource.index, 1)
    destClone.splice(droppableDestination.index, 0, removed)

    const result = {}
    result[droppableSource.droppableId] = sourceClone
    result[droppableDestination.droppableId] = destClone
    return result
}

const grid = 8

const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: 'none',
    padding: grid * 2,
    margin: `0 0 ${grid}px 0`,
    background: isDragging ? 'lightgreen' : '#eaeaea',
    borderRadius: '4px',
    ...draggableStyle,
})

const getListStyle = (isDraggingOver) => ({
    background: isDraggingOver ? 'lightblue' : '#f8f8f8',
    padding: grid,
    width: '50%',
    margin: '10px 0',
})

const VendorScheduleAdModal = ({ show, handleClose }) => {
    const [screens, setScreens] = useState([])
    const [selectedScreen, setSelectedScreen] = useState(null)
    const [allAds, setAllAds] = useState([])
    const [state, setState] = useState([[], []])
    const [spinner, setSpinner] = useState(false)
    const [screenSpinner, setScreenSpinner] = useState(false)
    const [adSpinner, setAdSpinner] = useState(false)
    const [saveSpinner, setSaveSpinner] = useState(false)
    const [searchKey, setSearchKey] = useState('')

    const [adTiming, setAdTiming] = useState({
        duration: 10,
        interval: 5
    })

    useEffect(() => {
        if (show) {
            loadScreens()
            loadAllAds()
        }
    }, [show])

    useEffect(() => {
        if (selectedScreen) {
            loadStoreAds()
        }
    }, [selectedScreen])

    const loadScreens = async () => {
        setScreenSpinner(true)
        try {
            const response = await axios.get(VendorAdScreensEnd, {
                headers: {
                    menuboard: localStorage.getItem('vendor_token'),
                },
            })
            if (response.status === 200) {
                const screenData = response.data.data || []
                setScreens(screenData)
            }
        } catch (error) {
            Toast('err', error.response?.data?.msg || 'Failed to load screens')
        }
        setScreenSpinner(false)
    }

    const loadAllAds = async () => {
        setAdSpinner(true)
        try {
            const response = await axios.get(VendorAdGetAllEnd, {
                headers: {
                    menuboard: localStorage.getItem('vendor_token'),
                },
            })
            if (response.status === 200) {
                setAllAds(response.data.data || [])
            }
        } catch (error) {
            Toast('err', error.response?.data?.msg || 'Failed to load ads')
        }
        setAdSpinner(false)
    }

    const loadStoreAds = async () => {
        setSpinner(true)
        try {
            const response = await axios.get(
                VendorAdStoreByScreenEnd + `?screen_id=${selectedScreen.value}`,
                {
                    headers: {
                        menuboard: localStorage.getItem('vendor_token'),
                    },
                }
            )
            if (response.status === 200) {
                const storeData = response.data.data
                if (storeData?.ad_timing) {
                    setAdTiming(storeData.ad_timing)
                }

                // Get vendor's ads that are already scheduled on this store
                const scheduledAds = storeData?.ads || []
                const vendorId = localStorage.getItem('vendor_id')

                // Filter to show only vendor's scheduled ads
                const vendorScheduledAds = scheduledAds
                    .filter(ad => ad.ad_id && ad.ad_id.vendor_id)
                    .map(ad => ad.ad_id)

                // Available ads (not scheduled)
                const scheduledAdIds = vendorScheduledAds.map(a => a._id)
                const availableAds = allAds.filter(ad => !scheduledAdIds.includes(ad._id))

                setState([availableAds, vendorScheduledAds])
            }
        } catch (error) {
            Toast('err', error.response?.data?.msg || 'Failed to load store data')
        }
        setSpinner(false)
    }

    const onDragEnd = (result) => {
        const { source, destination } = result

        if (!destination) {
            return
        }
        const sInd = +source.droppableId
        const dInd = +destination.droppableId

        if (sInd === dInd) {
            const items = reorder(state[sInd], source.index, destination.index)
            const newState = [...state]
            newState[sInd] = items
            setState(newState)
        } else {
            const result = move(state[sInd], state[dInd], source, destination)
            const newState = [...state]
            newState[sInd] = result[sInd]
            newState[dInd] = result[dInd]
            setState(newState)
        }
    }

    const handleSubmit = async () => {
        if (!selectedScreen) {
            Toast('err', 'Please select a screen first')
            return
        }

        setSaveSpinner(true)

        const adsToSchedule = state[1].map(ad => ({ ad_id: ad._id }))

        try {
            // Save ads
            const scheduleResponse = await axios.post(
                VendorAdScheduleEnd,
                {
                    screen_id: selectedScreen.value,
                    ads: adsToSchedule
                },
                {
                    headers: {
                        menuboard: localStorage.getItem('vendor_token'),
                    },
                }
            )

            if (scheduleResponse.status === 200) {
                // Save timing
                await axios.put(
                    VendorAdTimingEnd,
                    {
                        screen_id: selectedScreen.value,
                        ad_timing: adTiming
                    },
                    {
                        headers: {
                            menuboard: localStorage.getItem('vendor_token'),
                        },
                    }
                )

                Toast('success', 'Ads scheduled successfully!')
                handleClose()
            }
        } catch (error) {
            Toast('err', error.response?.data?.msg || 'Failed to schedule ads')
        }
        setSaveSpinner(false)
    }

    const screenOptions = screens.map(screen => ({
        value: screen._id,
        label: `${screen.screen_name} (${screen.store_id?.name || 'Unknown Store'})`
    }))

    const filteredAvailableAds = state[0].filter(ad =>
        ad.name?.toLowerCase().includes(searchKey.toLowerCase())
    )

    return (
        <Modal show={show} onHide={handleClose} size='xl'>
            <Modal.Header closeButton style={{ border: 'none' }}>
                <Modal.Title style={{ fontSize: '22px' }}>
                    Schedule Ads on Screens
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ overflowY: 'scroll', height: '80vh' }}>
                <Card className='mb-4'>
                    <Card.Body>
                        <div className='row'>
                            <div className='col-md-6'>
                                <Form.Group>
                                    <Form.Label><strong>Select Screen</strong></Form.Label>
                                    {screenSpinner ? (
                                        <div className='text-center'>
                                            <Spinner animation='border' size='sm' />
                                        </div>
                                    ) : (
                                        <Select
                                            options={screenOptions}
                                            value={selectedScreen}
                                            onChange={(option) => setSelectedScreen(option)}
                                            placeholder='Select a screen to schedule ads...'
                                            isClearable
                                        />
                                    )}
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <Form.Group>
                                    <Form.Label><strong>Ad Duration (sec)</strong></Form.Label>
                                    <Form.Control
                                        type='number'
                                        min='1'
                                        value={adTiming.duration}
                                        onChange={(e) => setAdTiming({
                                            ...adTiming,
                                            duration: parseInt(e.target.value) || 10
                                        })}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <Form.Group>
                                    <Form.Label><strong>Ad Interval (min)</strong></Form.Label>
                                    <Form.Control
                                        type='number'
                                        min='1'
                                        value={adTiming.interval}
                                        onChange={(e) => setAdTiming({
                                            ...adTiming,
                                            interval: parseInt(e.target.value) || 5
                                        })}
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {selectedScreen && (
                    <>
                        <div className='plain-input mb-3'>
                            <label><strong>Search Ads</strong></label>
                            <br />
                            <input
                                type='text'
                                placeholder='Enter ad name'
                                value={searchKey}
                                onChange={(e) => setSearchKey(e.target.value)}
                            />
                        </div>

                        {spinner ? (
                            <div className='text-center py-5'>
                                <Spinner animation='border' style={{ color: '#558f55' }} />
                            </div>
                        ) : (
                            <div style={{ display: 'flex', width: '100%' }}>
                                <DragDropContext onDragEnd={onDragEnd}>
                                    {[filteredAvailableAds, state[1]].map((el, ind) => (
                                        <Droppable key={ind} droppableId={`${ind}`}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    style={getListStyle(snapshot?.isDraggingOver)}
                                                    {...provided.droppableProps}
                                                >
                                                    <h5 className='mb-4 d-flex justify-content-start align-items-center'>
                                                        {ind === 0 ? 'Available Ads' : 'Scheduled for Screen'}
                                                        {ind === 0 && adSpinner && (
                                                            <Spinner
                                                                animation='border'
                                                                size='sm'
                                                                className='ms-2'
                                                            />
                                                        )}
                                                        {ind === 1 && (
                                                            <Badge bg='success' className='ms-2'>
                                                                {state[1].length}
                                                            </Badge>
                                                        )}
                                                    </h5>
                                                    {(ind === 0 ? filteredAvailableAds : el).map((item, index) => (
                                                        <Draggable
                                                            key={item?._id}
                                                            draggableId={item?._id}
                                                            index={index}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    style={getItemStyle(
                                                                        snapshot.isDragging,
                                                                        provided.draggableProps.style
                                                                    )}
                                                                >
                                                                    <div className='row justify-content-between align-items-center'>
                                                                        <div className='col-5 d-flex'>
                                                                            <h6 className='me-2'>{index + 1}.</h6>
                                                                            <div>
                                                                                <h6 className='mb-0'>{item?.name}</h6>
                                                                                <small className='text-muted'>
                                                                                    {item?.type}
                                                                                </small>
                                                                            </div>
                                                                        </div>

                                                                        {item?.type === 'photo' ? (
                                                                            <img
                                                                                className='col-4'
                                                                                src={item?.link}
                                                                                alt=''
                                                                                height='60px'
                                                                                width='80px'
                                                                                style={{ objectFit: 'cover', borderRadius: '4px' }}
                                                                            />
                                                                        ) : (
                                                                            <ReactPlayer
                                                                                className='col-4'
                                                                                url={item?.link}
                                                                                width='80px'
                                                                                height='60px'
                                                                                controls={false}
                                                                            />
                                                                        )}
                                                                        {ind !== 0 && (
                                                                            <div className='col-1 mx-2 text-center'>
                                                                                <MdDeleteForever
                                                                                    style={{
                                                                                        height: '40px',
                                                                                        width: '25px',
                                                                                        cursor: 'pointer',
                                                                                    }}
                                                                                    className='text-danger'
                                                                                    onClick={() => {
                                                                                        const newState = [...state]
                                                                                        newState[1].splice(index, 1)
                                                                                        newState[0].push(item)
                                                                                        setState(newState)
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                    {el.length === 0 && ind === 0 && (
                                                        <p className='text-muted text-center py-4'>
                                                            No ads available. Upload ads first.
                                                        </p>
                                                    )}
                                                    {el.length === 0 && ind === 1 && (
                                                        <p className='text-muted text-center py-4'>
                                                            Drag ads here to schedule them.
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </Droppable>
                                    ))}
                                </DragDropContext>
                            </div>
                        )}
                    </>
                )}

                {!selectedScreen && (
                    <div className='text-center py-5'>
                        <h5 className='text-muted'>Select a screen to schedule ads</h5>
                        <p className='text-muted'>
                            Choose from your assigned screens to manage advertisements.
                        </p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer style={{ border: 'none' }}>
                <button className='primary-btn-light' onClick={handleClose}>
                    Close
                </button>
                <button
                    className='primary-btn d-flex justify-content-center align-items-center'
                    onClick={handleSubmit}
                    disabled={saveSpinner || !selectedScreen}
                >
                    Save Changes{' '}
                    {saveSpinner && <Spinner className='ms-2' animation='border' size='sm' />}
                </button>
            </Modal.Footer>
        </Modal>
    )
}

export default VendorScheduleAdModal
